import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import type { Ticket } from '../../../store/features/tickets/types';
import { resolveClientDisplayNameFromTicket } from '../../../lib/clientDisplayName';
import InternalAssigneeIdentity from './InternalAssigneeIdentity';

const Wrap = styled.div`
  padding: 0;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  height: clamp(360px, 60vh, 640px);
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,.06);
  background: linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96));
  box-shadow: 0 18px 48px rgba(0,0,0,.45);
  color: #E8EAED;
  color-scheme: dark;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);
`;

const Title = styled.div`
  font-weight: 900;
  letter-spacing: .3px;
  color: #E8EAED;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const Scroll = styled.div`
  overflow: auto;
  color-scheme: dark;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const DesktopOnly = styled.div`
  display: block;
  @media (max-width: 767px) { display: none; }
`;

const Table = styled.table`
  width: 100%;
  min-width: 1120px;
  border-collapse: separate;
  border-spacing: 0;
  color: #E8EAED;
  font-size: 14px;

  th, td {
    padding: 12px 14px;
    text-align: left;
    white-space: nowrap;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-weight: 900;
    letter-spacing: .3px;
    background: #1f2430;
    border-bottom: 1px solid rgba(255,255,255,.10);
  }

 tbody td { border-bottom: 1px solid rgba(255,255,255,.08); }
  tbody tr:hover td { background: rgba(255,255,255,.03); cursor: pointer; }
`;

const StatusPill = styled.span<{ $bg: string }>`
  padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 800;
  color: #111827; background: ${({ $bg }) => $bg};
`;

const Dot = styled.span<{ $color: string }>`
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const EmptyState = styled.div`
  padding: 24px;
  color: #cbd2e1;
`;

const SkeletonBar = styled.div<{ $width?: string; $height?: string; $rounded?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '14px'};
  border-radius: ${({ $rounded }) => $rounded || '8px'};
  background: linear-gradient(
    90deg,
    rgba(255,255,255,.06) 0%,
    rgba(255,255,255,.12) 50%,
    rgba(255,255,255,.06) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.35s linear infinite;
`;

const SkeletonPill = styled(SkeletonBar)`
  height: 24px;
  border-radius: 999px;
`;

const SkeletonCellStack = styled.div`
  display: grid;
  gap: 8px;
`;

type UiStatus = 'submitted' | 'in_progress' | 'blocked' | 'resolved' | 'closed';

function mapStatusToUi(s?: string): UiStatus {
    switch (s) {
        case 'in_progress': return 'in_progress';
        case 'blocked': return 'blocked';
        case 'resolved': return 'resolved';  // ← show “resolved” (not “delivered”)
        case 'closed': return 'closed';
        case 'open':
        default: return 'submitted';
    }
}

function toneFor(s: UiStatus) {
    switch (s) {
        case 'in_progress': return '#A7F3D0';
        case 'blocked': return '#FDE68A';
        case 'resolved': return '#93C5FD';
        case 'submitted': return '#E5E7EB';
        case 'closed': return '#D1D5DB';
        default: return '#E5E7EB';
    }
}

/** P0..P3 -> color */
function priorityDot(p?: Ticket['priority']) {
    switch (p) {
        case 'P0': return '#EF4444';
        case 'P1': return '#F59E0B';
        case 'P2': return '#3B82F6';
        case 'P3':
        default: return '#9CA3AF';
    }
}



// UI-only title fallback (never write placeholders to DB)
function renderTitle(raw: unknown) {
  const s = typeof raw === 'string' ? raw.trim() : String(raw ?? '').trim();
  return s ? s : <em style={{ color: '#9aa4b2' }}>Untitled ticket</em>;
}

function renderTicketId(ticket: Ticket) {
  const ticketNumber = String((ticket as any).ticketNumber ?? '').trim();
  if (!ticketNumber) return '--';
  return <span style={{ color: '#8DEBFF', fontWeight: 900 }}>#{ticketNumber}</span>;
}



// ---- robust timestamp helpers (Admin/Client SDK, ms, seconds, ISO) ----
function tsToDate(value: any): Date | null {
    if (!value) return null;
    if (typeof value?.toDate === 'function') return value.toDate();
    const s = value.seconds ?? value._seconds;
    if (typeof s === 'number') return new Date(s * 1000);
    if (typeof value === 'number') return new Date(value > 1e12 ? value : value * 1000);
    if (typeof value === 'string') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
}

function formatYMD(d: Date | null): string {
    if (!d) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}


type Props = {
    /** Raw tickets as returned from the backend/store for this view */
    tickets: Ticket[];
    /** Minimal project shape (id + optional name) to show the Project column */
    projects: Array<{ id: string; name?: string }>;
    /** Row click */
    onOpen: (id: string) => void;
    /** Optional title for the table header */
    title?: string;
    loading?: boolean;
};

export default function AgentTicketsTable({
    tickets, projects, onOpen, title = 'My Tickets', loading = false
}: Props) {
    // Build a defensive index that tolerates slightly different shapes,
  // even though the page now normalizes them for us.
  const byProject = useMemo(() => {
    const m = new Map<string, { id: string; name?: string }>();
    for (const raw of projects || []) {
      const p: any = raw;
      const id =
        p?.id ??
        p?.projectId ??
        p?.uid ??
        p?.key ??
        null;
      if (!id) continue;
      const name =
        p?.name ??
        p?.title ??
        p?.label ??
        p?.code ??
        undefined;
      m.set(String(id), { id: String(id), name });
    }
    return m;
  }, [projects]);

    const isEmpty = !loading && (!tickets || tickets.length === 0);
    const skeletonRows = useMemo(() => Array.from({ length: 7 }, (_, index) => index), []);

    return (
        <Wrap>
            <TopBar>
                <Title>{title}</Title>
            </TopBar>

            <Scroll>
                {loading ? (
                    <DesktopOnly>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Ticket ID</th>
                                    <th>Title</th>
                                    <th>Status</th>
                                    <th>Priority</th>
                                    <th>Project</th>
                                    <th>Client</th>
                                    <th>Assigned</th>
                                    <th>Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {skeletonRows.map((row) => (
                                    <tr key={`agent-ticket-skeleton-${row}`}>
                                        <td><SkeletonBar $width="92px" /></td>
                                        <td>
                                            <SkeletonCellStack>
                                                <SkeletonBar $width="66%" />
                                                <SkeletonBar $width="42%" $height="12px" />
                                            </SkeletonCellStack>
                                        </td>
                                        <td><SkeletonPill $width="96px" /></td>
                                        <td><SkeletonBar $width="88px" /></td>
                                        <td><SkeletonBar $width="132px" /></td>
                                        <td><SkeletonBar $width="144px" /></td>
                                        <td><SkeletonBar $width="110px" /></td>
                                        <td><SkeletonBar $width="84px" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </DesktopOnly>
                ) : isEmpty ? (
                    <EmptyState>No tickets found.</EmptyState>
                ) : (
                    <DesktopOnly>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Ticket ID</th>
                                    <th>Title</th>
                                    <th>Status</th>
                                    <th>Priority</th>
                                    <th>Project</th>
                                    <th>Client</th>
                                    <th>Assigned</th>
                                    <th>Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.map(t => {
                                    const ui = mapStatusToUi(String(t.status));
                                    const projName = t.projectId
        ? (
            // 1) server-enriched list (if you ever add it)
            (t as any).projectName ??
            // 2) normalized index we built from Redux projects
            byProject.get(String(t.projectId))?.name ??
            // 3) tolerate a ticket-embedded projects[] shape (drawer uses this)
            (Array.isArray((t as any).projects)
              ? (t as any).projects.find((p: any) => String(p.id) === String(t.projectId))?.name
              : undefined) ??
            // 4) other ad-hoc fallbacks we’ve seen in codebases
            (t as any).project?.name ??
            (t as any).projectLabel ??
            '—'
          )
        : '—';

                                    const updatedRaw =
                                        (t as any).updatedAt ??
                                        (t as any).updated ??
                                        (t as any).updated_at ??
                                        (t as any).createdAt ??
                                        (t as any).created ??
                                        (t as any).created_at ??
                                        (t as any).created_on ??
                                        null;
                                    const updatedDate = tsToDate(updatedRaw);
                                    return (
                                        <tr key={t.id} onClick={() => onOpen(t.id)}>
                                            <td>{renderTicketId(t)}</td>
                                            <td>{renderTitle(t.title)}</td>
                                            <td>
                                                <StatusPill $bg={toneFor(ui)}>{ui.replace(/_/g, ' ')}</StatusPill>
                                            </td>
                                            <td>
                                                <Dot $color={priorityDot(t.priority)} /> <span style={{ marginLeft: 8 }}>{t.priority ?? '—'}</span>
                                            </td>
                                            <td>{projName}</td>
                                            <td>{resolveClientDisplayNameFromTicket(t, '--')}</td>
                                            <td><InternalAssigneeIdentity source={t as any} /></td>
                                            <td title={updatedDate ? updatedDate.toLocaleString() : ''}>
                                                {formatYMD(updatedDate)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </DesktopOnly>
                )}
            </Scroll>
        </Wrap>
    );
}
